import { auth } from '../lib/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  PhoneAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  applyActionCode,
} from 'firebase/auth';
import { createUserProfile, type UserProfile } from './userService';

export async function signupWithEmail(email: string, password: string, displayName?: string) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) await updateProfile(cred.user, { displayName });
  
  // Create user profile in Firestore
  const userProfile: UserProfile = {
    uid: cred.user.uid,
    name: displayName || '',
    username: displayName?.toLowerCase().replace(/\s+/g, '') || '',
    email: cred.user.email || '',
    avatarUrl: cred.user.photoURL || '',
    createdAt: new Date(),
    updatedAt: new Date()
  };
  
  await createUserProfile(userProfile);
  return cred.user;
}

export async function loginWithEmail(email: string, password: string) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(auth, provider);
  
  // Create user profile in Firestore if it doesn't exist
  try {
    const userProfile: UserProfile = {
      uid: cred.user.uid,
      name: cred.user.displayName || '',
      username: cred.user.displayName?.toLowerCase().replace(/\s+/g, '') || '',
      email: cred.user.email || '',
      avatarUrl: cred.user.photoURL || '',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await createUserProfile(userProfile);
  } catch (error) {
    // Silently fail - user profile might already exist
  }
  
  return cred.user;
}

export async function resetPassword(email: string) {
  await sendPasswordResetEmail(auth, email);
}

export async function logout() {
  await signOut(auth);
}

// Phone Authentication
let recaptchaVerifier: RecaptchaVerifier | null = null;

export function initializeRecaptcha(containerId: string) {
  if (!recaptchaVerifier) {
    recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
      'expired-callback': () => {
        // reCAPTCHA expired
      }
    });
  }
  return recaptchaVerifier;
}

export async function sendPhoneVerification(phoneNumber: string): Promise<ConfirmationResult> {
  if (!recaptchaVerifier) {
    throw new Error('reCAPTCHA not initialized');
  }
  
  const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
  return confirmationResult;
}

export async function verifyPhoneCode(confirmationResult: ConfirmationResult, code: string) {
  const result = await confirmationResult.confirm(code);
  return result.user;
}

export async function signupWithPhone(phoneNumber: string, userData: Partial<UserProfile>) {
  // Phone number is already verified at this point
  const userProfile: UserProfile = {
    uid: auth.currentUser?.uid || '',
    phoneNumber,
    isPhoneVerified: true,
    ...userData,
    createdAt: new Date(),
    updatedAt: new Date()
  };
  
  await createUserProfile(userProfile);
  return auth.currentUser;
}

export async function sendEmailVerificationToUser() {
  if (!auth.currentUser) {
    throw new Error('No user is currently signed in');
  }
  
  if (auth.currentUser.emailVerified) {
    // Check if this is a Google sign-in account
    const isGoogleAccount = auth.currentUser.providerData.some(provider => provider.providerId === 'google.com');
    if (isGoogleAccount) {
      throw new Error('Google sign-in accounts are automatically email verified. No verification email needed.');
    } else {
      throw new Error('Email is already verified');
    }
  }
  
  try {
    // Get the current origin (works for both localhost and production)
    const currentOrigin = window.location.origin;
    const continueUrl = `${currentOrigin}/verify-email`;
    
    // Send email verification with custom continue URL
    // After user clicks the link, Firebase will redirect to our /verify-email page
    await sendEmailVerification(auth.currentUser, {
      url: continueUrl,
      handleCodeInApp: false, // Firebase handles the redirect after verification
    });
    return true;
  } catch (error: any) {
    console.error('Error sending email verification:', error);
    throw new Error(`Failed to send verification email: ${error.message}`);
  }
}

export async function verifyEmail() {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser.emailVerified;
  }
  return false;
}

export async function applyEmailVerificationCode(actionCode: string) {
  try {
    await applyActionCode(auth, actionCode);
    return true;
  } catch (error: any) {
    console.error('Error applying email verification code:', error);
    throw new Error(`Failed to verify email: ${error.message}`);
  }
}


